import { useWorkspaceStore } from "@/store/workspace-store";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useActiveOrg } from "@/hooks/use-active-org";
import { Skeleton } from "../ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { getDashboardData } from "@/api/deals-api";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { CompanyAvatar } from "../deals/company-avatar";

// ── Helpers ───────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const sectionLabelClass =
  "text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60";

const cellLabelClass =
  "mt-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 whitespace-nowrap";

const cellCountClass = (count: number) =>
  cn(
    "font-mono text-lg font-semibold tabular-nums",
    count === 0 ? "text-muted-foreground/40" : "text-foreground"
  );

const gridCellClass =
  "flex flex-col items-center justify-center bg-background px-3 py-3 text-center transition-colors hover:bg-foreground/[0.03]";

// ── Component ─────────────────────────────────────────────────────────────────

export const DashboardComponent = () => {
  const { activeWorkspace, activeWorkspaceId } = useWorkspaceStore();
  const { quota } = useOrgQuota();
  const { billingHref: billingLink } = useActiveOrg();
  const [pipelineScrolled, setPipelineScrolled] = useState(false);

  const { data: dashboardApiData, isLoading } = useQuery({
    queryKey: ["dashboard", activeWorkspaceId],
    queryFn: () => getDashboardData(activeWorkspaceId!),
    enabled: !!activeWorkspaceId,
  });

  const isPageLoading = !activeWorkspace || isLoading;

  // Build pipeline snapshot from workspace statuses + API data
  const pipelineSnapshot = useMemo(() => {
    if (!activeWorkspace?.statuses || !dashboardApiData?.data) return [];
    const stages = dashboardApiData.data.pipeline_stages || {};
    return activeWorkspace.statuses.map((status) => ({
      stage: status.title,
      count:
        stages[status.title as keyof typeof stages] ?? 0,
    }));
  }, [activeWorkspace?.statuses, dashboardApiData?.data]);

  // Recent deals now come from the dashboard API (already ordered, top 4),
  // so the page needs no separate deals-list fetch.
  const recentDeals = useMemo(
    () => (dashboardApiData?.data.recent_deals ?? []).slice(0, 4),
    [dashboardApiData?.data.recent_deals]
  );

  const workspaceName = activeWorkspace?.opportunity_name;

  if (isPageLoading) {
    return (
      <div className="flex h-full flex-col overflow-y-auto bg-background">
        <div className="mx-auto w-full max-w-2xl px-6 py-12">
          <Skeleton className="h-8 w-48 mb-8" />
          <Skeleton className="h-5 w-32 mb-3" />
          <Skeleton className="h-48 w-full rounded-lg mb-8" />
          <Skeleton className="h-5 w-24 mb-3" />
          <Skeleton className="h-20 w-full rounded-lg mb-8" />
          <Skeleton className="h-5 w-32 mb-3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  const stats = dashboardApiData?.data;
  const dealsHref = `/${activeWorkspaceId}/deals`;
  const portcoHref = `/${activeWorkspaceId}/portco`;
  const stageHref = (stage: string) =>
    `${dealsHref}?status=${encodeURIComponent(stage)}`;

  return (
    <div className="flex h-full flex-col items-center overflow-y-auto bg-background">
      <div className="my-auto w-full max-w-2xl px-6 py-12">
        {/* Greeting + workspace identity */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-foreground">
            {greeting()}.
          </h1>
          {(workspaceName || quota?.plan) && (
            <p className="mt-1.5 font-mono text-[11px] text-muted-foreground/60">
              {workspaceName}
              {workspaceName && quota?.plan && " · "}
              {quota?.plan &&
                (billingLink ? (
                  <Link
                    to={billingLink}
                    className="hover:text-foreground transition-colors"
                    title="Manage subscription"
                  >
                    {quota.plan} plan
                  </Link>
                ) : (
                  <span>{quota.plan} plan</span>
                ))}
            </p>
          )}
        </div>

        {/* Pipeline Snapshot */}
        {pipelineSnapshot.length > 0 && (
          <section className="mb-8">
            <div className="mb-2 flex items-center justify-between">
              <h2 className={sectionLabelClass}>Pipeline</h2>
              <Link
                to={dealsHref}
                className={cn(
                  sectionLabelClass,
                  "text-muted-foreground/40 hover:text-foreground transition-colors"
                )}
              >
                View all &rarr;
              </Link>
            </div>

            <div className="relative -mx-6 sm:hidden">
              <div
                onScroll={(e) =>
                  setPipelineScrolled(e.currentTarget.scrollLeft > 4)
                }
                className="flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {pipelineSnapshot.map((s) => (
                  <Link
                    key={s.stage}
                    to={stageHref(s.stage)}
                    className="flex shrink-0 flex-col items-center justify-center rounded-lg border border-border bg-background px-5 py-3 text-center transition-colors hover:bg-foreground/[0.03]"
                  >
                    <span className={cellCountClass(s.count)}>{s.count}</span>
                    <span className={cellLabelClass}>{s.stage}</span>
                  </Link>
                ))}
              </div>
              {/* Right-edge fade so the clipped last card reads as a carousel. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-background to-transparent"
              />
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-background to-transparent transition-opacity duration-150",
                  pipelineScrolled ? "opacity-100" : "opacity-0"
                )}
              />
            </div>

            <div
              className="hidden sm:grid gap-px overflow-hidden rounded-lg border border-border bg-border"
              style={{
                gridTemplateColumns: `repeat(${Math.min(pipelineSnapshot.length, 8)}, minmax(0, 1fr))`,
              }}
            >
              {pipelineSnapshot.map((s) => (
                <Link
                  key={s.stage}
                  to={stageHref(s.stage)}
                  className={gridCellClass}
                >
                  <span className={cellCountClass(s.count)}>{s.count}</span>
                  <span className={cn(cellLabelClass, "whitespace-normal leading-tight")}>
                    {s.stage}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Quick Stats */}
        {stats && (
          <section className="mb-8">
            <div className="mb-2 flex items-center justify-between">
              <h2 className={sectionLabelClass}>Overview</h2>
            </div>
            <div className="grid grid-cols-4 gap-px overflow-hidden rounded-lg border border-border bg-border">
              <Link to={dealsHref} className={gridCellClass}>
                <span className={cellCountClass(stats.total_deals || 0)}>
                  {stats.total_deals || 0}
                </span>
                <span className={cellLabelClass}>Total Deals</span>
              </Link>
              <div className={gridCellClass}>
                <span className={cellCountClass(stats.reports_generated || 0)}>
                  {stats.reports_generated || 0}
                </span>
                <span className={cellLabelClass}>Reports</span>
              </div>
              <div className={gridCellClass}>
                <span className={cellCountClass(stats.total_reviews || 0)}>
                  {stats.total_reviews || 0}
                </span>
                <span className={cellLabelClass}>Reviews</span>
              </div>
              <Link to={portcoHref} className={gridCellClass}>
                <span className={cellCountClass(stats.portfolio_companies || 0)}>
                  {stats.portfolio_companies || 0}
                </span>
                <span className={cellLabelClass}>Portfolio</span>
              </Link>
            </div>
          </section>
        )}

        {/* Recent deals */}
        {recentDeals.length > 0 && (
          <section className="mb-8">
            <div className="mb-2 flex items-center justify-between">
              <h2 className={sectionLabelClass}>Recent</h2>
              <Link
                to={dealsHref}
                className={cn(
                  sectionLabelClass,
                  "text-muted-foreground/40 hover:text-foreground transition-colors"
                )}
              >
                View all &rarr;
              </Link>
            </div>
            <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
              {recentDeals.map((deal) => (
                <Link
                  key={deal.id}
                  to={`${dealsHref}/${deal.id}`}
                  className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-foreground/[0.03]"
                >
                  <CompanyAvatar
                    companyFiles={deal.company_files}
                    websiteUrl={deal.website}
                    alt={deal.name}
                    className="h-6 w-6 shrink-0"
                    iconClassName="h-3.5 w-3.5"
                  />
                  <span className="flex-1 truncate text-sm font-medium text-foreground">
                    {deal.name}
                  </span>
                  <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 whitespace-nowrap">
                    {deal.status}
                  </span>
                  <span className="font-mono text-[11px] tabular-nums text-muted-foreground/60 whitespace-nowrap">
                    {formatDistanceToNow(new Date(deal.added_on).getTime(), {
                      addSuffix: true,
                    })}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

      </div>
    </div>
  );
};
